import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  ADD_ADDITIONAL_DETAILS,
  ADD_NOTE_TO_BOOKING,
  UPDATE_SALESFORCE_BOOKING_STATUS,
  UPDATE_SALESFORCE_STAFF_VOUCHERS_FOR_BOOKING
} from 'store/mutations';
import { useMutation } from '@apollo/client';

import { customerShape, paymentsShape } from 'constants/propTypes';
import { codes, COVID_VOUCHER_REFUND, MARGIN_REFUND } from 'constants/payment';

import { CurrencyInput } from 'components/ui/Inputs';
import ValidationForm from 'components/ui/ValidationForm';
import { getAmountToRefundPerMethod } from 'components/ui/Payments/Payments.utils';

import SplitPayment from '../../SplitPayment';
import { handleRefundSubmit } from '../../SplitPayment/SplitPayment.utils';
import { Errors, Successes } from '../../SplitPayment/SplitPayment.styles';
import { generate } from 'short-uuid';

const StandardRefund = ({
  title,
  loading,
  totalPriceToBePaid,
  addPayment,
  addPayoutAndBalance,
  updatePayments,
  customer,
  reference,
  bookingReference,
  currency,
  method,
  isCancelVoucher,
  mainBookingRef,
  isRefundingEntireBlueVoucherBooking,
  payments,
  isOverpaid = false
}) => {
  const [addNote] = useMutation(ADD_NOTE_TO_BOOKING);
  const [refundBlueVoucherBooking, { error: refundBlueVoucherBookingError }] = useMutation(UPDATE_SALESFORCE_STAFF_VOUCHERS_FOR_BOOKING);
  const [updateSalesforceBookingStatus] = useMutation(UPDATE_SALESFORCE_BOOKING_STATUS);
  const [addAdditionalDetails] = useMutation(ADD_ADDITIONAL_DETAILS);

  let initialAmount = totalPriceToBePaid;
  let totalBlueVouchers = 0;
  let blueVoucherPaymentRefId = 0;

  if (isRefundingEntireBlueVoucherBooking) {
    initialAmount = getAmountToRefundPerMethod(payments, codes.BLUE_VOUCHER);
    totalBlueVouchers = payments.find((payment) => payment.method === codes.BLUE_VOUCHER).reference.split('-')[0];
    blueVoucherPaymentRefId = Number(payments.find((payment) => payment.method === codes.BLUE_VOUCHER).refId);
  }

  return (
    <ValidationForm
      fields={{ amount: initialAmount }}
      validators={{
        amount: (values) => {
          const amount = Number(values.amount);

          return amount !== 0 && amount <= totalPriceToBePaid;
        }
      }}
    >
      {({ fields: { amount }, onChange, invalidInputs, hasInvalidInputs }) => (
        <SplitPayment
          title={title}
          subTitle={<FormattedMessage id="refunds.canBeSplit" />}
          label={<FormattedMessage id="refunds.recordRefund" />}
          loading={loading}
          onSubmit={(e) => {
            e.preventDefault();

            const completeTransaction = amount >= totalPriceToBePaid - MARGIN_REFUND;

            handleRefundSubmit({
              membershipNumber: null,
              completeTransaction,
              paymentAmount: amount,
              totalPriceToBePaid,
              addPayoutAndBalance,
              updatePayments,
              addPayment,
              customer,
              bookingReference,
              reference,
              currency,
              method,
              isRefundingEntireBlueVoucherBooking,
              blueVoucherAmount: totalBlueVouchers,
              isOverpaid
            });

            if (method === codes.BLUE_VOUCHER && isRefundingEntireBlueVoucherBooking) {
              refundBlueVoucherBooking({
                variables: {
                  bookingReference,
                  vouchersUsed: 0
                }
              });

              updateSalesforceBookingStatus({
                variables: {
                  bookingReference,
                  status: 'CANCELLED'
                }
              });

              addAdditionalDetails({
                variables: {
                  bookingReference,
                  additionalDetails: [
                    {
                      key: `REFUND-${generate()}`,
                      value: totalBlueVouchers,
                      paymentRefId: blueVoucherPaymentRefId
                    }
                  ]
                }
              });
            }

            if (isCancelVoucher) {
              addNote({
                variables: {
                  note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
                  bookingReference: mainBookingRef
                }
              });

              addNote({
                variables: {
                  note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
                  bookingReference
                }
              });
            }
          }}
          disabled={hasInvalidInputs}
        >
          <CurrencyInput
            name="amount"
            value={amount}
            placeHolder={0}
            currency={currency}
            onChange={onChange}
            disabled={isRefundingEntireBlueVoucherBooking}
          />

          <br />

          {isRefundingEntireBlueVoucherBooking && (
            <FormattedMessage id="refunds.blueVoucher.totalRefund" values={{ numberOfVouchers: totalBlueVouchers }} />
          )}

          <Errors>
            {invalidInputs.amount && Number(amount) === 0 && <FormattedMessage id="payments.amountZero" />}
            {invalidInputs.amount && Number(amount) !== 0 && <FormattedMessage id="payments.amountTooLarge" />}
            {method === codes.BLUE_VOUCHER && !isRefundingEntireBlueVoucherBooking && (
              <FormattedMessage id="refunds.warning.blueVouchersManualSalesforce" />
            )}
            {method === codes.BLUE_VOUCHER && refundBlueVoucherBookingError && (
              <FormattedMessage id="refunds.warning.blueVouchersManualSalesforce" />
            )}
          </Errors>

          <Successes>{isRefundingEntireBlueVoucherBooking && <FormattedMessage id="refunds.blueVoucher.autoSalesforce" />}</Successes>
        </SplitPayment>
      )}
    </ValidationForm>
  );
};

StandardRefund.propTypes = {
  title: PropTypes.node,
  bookingReference: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  loading: PropTypes.bool,
  addPayment: PropTypes.func,
  addPayoutAndBalance: PropTypes.func,
  updatePayments: PropTypes.func,
  customer: customerShape,
  reference: PropTypes.string,
  currency: PropTypes.string,
  method: PropTypes.string,
  isCancelVoucher: PropTypes.bool,
  mainBookingRef: PropTypes.string,
  isRefundingEntireBlueVoucherBooking: PropTypes.bool,
  payments: paymentsShape,
  isOverpaid: PropTypes.bool
};

export default StandardRefund;
